import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, TrendingUp, PieChart, BarChart3, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, ComposedChart, Line
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [expensesByStore, setExpensesByStore] = useState(null);
  const [expensesDetailed, setExpensesDetailed] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [ivaSummary, setIvaSummary] = useState(null);
  const [byAccountReport, setByAccountReport] = useState(null);
  const [libroMayor, setLibroMayor] = useState(null);
  const [libroMayorFilter, setLibroMayorFilter] = useState('');  // INGRESO, GASTO, or '' for all
  const [libroMayorViewMode, setLibroMayorViewMode] = useState('grouped'); // 'grouped' or 'ungrouped'
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [accountCodes, setAccountCodes] = useState('');
  const [filters, setFilters] = useState({
    store_id: 'all',
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      console.error('Error al cargar tiendas');
    }
  };

  const fetchIncomeStatement = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/income-statement`, {
        params: {
          store_id: (filters.store_id && filters.store_id !== 'all') ? filters.store_id : undefined,
          start_date: filters.start_date,
          end_date: filters.end_date
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomeStatement(response.data);
    } catch (error) {
      toast.error('Error al generar estado de resultados');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/balance-sheet`, {
        params: {
          store_id: (filters.store_id && filters.store_id !== 'all') ? filters.store_id : undefined,
          date: filters.date
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalanceSheet(response.data);
    } catch (error) {
      toast.error('Error al generar balance general');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpensesByStore = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/expenses-by-store`, {
        params: {
          start_date: filters.start_date,
          end_date: filters.end_date
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpensesByStore(response.data);
    } catch (error) {
      toast.error('Error al generar reporte de gastos');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpensesDetailed = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/expenses-detailed`, {
        params: {
          store_id: (filters.store_id && filters.store_id !== 'all') ? filters.store_id : undefined,
          start_date: filters.start_date,
          end_date: filters.end_date
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpensesDetailed(response.data);
    } catch (error) {
      toast.error('Error al generar reporte de gastos detallados');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitability = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/profitability-by-store`, {
        params: {
          start_date: filters.start_date,
          end_date: filters.end_date
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfitability(response.data);
    } catch (error) {
      toast.error('Error al generar reporte de rentabilidad');
    } finally {
      setLoading(false);
    }
  };

  const fetchIvaSummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      const response = await axios.get(`${API}/reports/iva-summary`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setIvaSummary(response.data);
    } catch (error) {
      toast.error('Error al generar reporte de IVA');
    } finally {
      setLoading(false);
    }
  };

  const fetchByAccountReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        store_id: filters.store_id !== 'all' ? filters.store_id : undefined,
        account_codes: accountCodes || undefined
      };
      const response = await axios.get(`${API}/reports/by-account`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setByAccountReport(response.data);
    } catch (error) {
      toast.error('Error al generar reporte por cuenta');
    } finally {
      setLoading(false);
    }
  };

  const fetchLibroMayor = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        account_type: libroMayorFilter || undefined
      };
      const response = await axios.get(`${API}/reports/libro-mayor`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibroMayor(response.data);
    } catch (error) {
      toast.error('Error al generar Libro Mayor');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountExpanded = (accountCode) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountCode]: !prev[accountCode]
    }));
  };

  const downloadExcel = async (reportType) => {
    try {
      const token = localStorage.getItem('token');
      
      let params = {};
      if (reportType === 'balance-sheet') {
        params = {
          store_id: (filters.store_id && filters.store_id !== 'all') ? filters.store_id : undefined,
          date: filters.date
        };
      } else {
        params = {
          store_id: (filters.store_id && filters.store_id !== 'all') ? filters.store_id : undefined,
          start_date: filters.start_date,
          end_date: filters.end_date
        };
      }
      
      const response = await axios.get(
        `${API}/reports/export/excel/${reportType}`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = reportType === 'balance-sheet' 
        ? `${reportType}_${filters.date}.xlsx`
        : `${reportType}_${filters.start_date}_${filters.end_date}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar reporte');
    }
  };

  const downloadPDF = async (reportType) => {
    try {
      const token = localStorage.getItem('token');
      
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      
      const response = await axios.get(
        `${API}/reports/export/pdf/${reportType}`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `${reportType}_${filters.start_date}_${filters.end_date}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar reporte PDF');
    }
  };

  const downloadLibroMayorPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        account_type: libroMayorFilter || undefined
      };
      
      const response = await axios.get(
        `${API}/reports/export/pdf/libro-mayor`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `libro_mayor_${filters.start_date}_${filters.end_date}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Libro Mayor PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar Libro Mayor PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Reportes</h1>
        <p className="text-slate-600 mt-1">Informes financieros y análisis contable</p>
      </div>

      <Tabs defaultValue="income-statement" className="space-y-6">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="income-statement" data-testid="income-statement-tab">Estado de Resultados</TabsTrigger>
          <TabsTrigger value="balance-sheet" data-testid="balance-sheet-tab">Balance General</TabsTrigger>
          <TabsTrigger value="libro-mayor" data-testid="libro-mayor-tab">Libro Mayor</TabsTrigger>
          <TabsTrigger value="iva-summary" data-testid="iva-summary-tab">IVA</TabsTrigger>
          <TabsTrigger value="by-account" data-testid="by-account-tab">Por Cuenta</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="expenses-tab">Gastos Resumen</TabsTrigger>
          <TabsTrigger value="expenses-detailed" data-testid="expenses-detailed-tab">Gastos Detallados</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="profitability-tab">Rentabilidad</TabsTrigger>
        </TabsList>

        {/* Estado de Resultados */}
        <TabsContent value="income-statement" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Configuración del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tienda</Label>
                  <select
                    value={filters.store_id}
                    onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="all">Consolidado (Todas)</option>
                    {stores.filter(s => s.is_active).map(store => (
                      <option key={`income-store-${store.id}`} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button onClick={fetchIncomeStatement} className="btn-primary" disabled={loading}>
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
                {incomeStatement && (
                  <Button onClick={() => downloadExcel('income-statement')} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Excel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {incomeStatement && (
            <Card className="hover-lift" data-testid="income-statement-result">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-900">
                  Estado de Resultados - {incomeStatement.store_name}
                </CardTitle>
                <p className="text-sm text-slate-600">
                  Periodo: {filters.start_date} a {filters.end_date}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border-b border-slate-200 pb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-slate-900">INGRESOS</span>
                      <span className="text-lg font-bold text-green-700 font-mono">
                        {formatCurrency(incomeStatement.ingresos)}
                      </span>
                    </div>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-slate-900">COSTOS DE VENTA</span>
                      <span className="text-lg font-bold text-red-700 font-mono">
                        ({formatCurrency(incomeStatement.costos)})
                      </span>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-slate-900">UTILIDAD BRUTA</span>
                      <span className="text-xl font-bold text-blue-700 font-mono">
                        {formatCurrency(incomeStatement.utilidad_bruta)}
                      </span>
                    </div>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-slate-900">GASTOS OPERATIVOS</span>
                      <span className="text-lg font-bold text-red-700 font-mono">
                        ({formatCurrency(incomeStatement.gastos_operativos)})
                      </span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-2 border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-slate-900">UTILIDAD NETA</span>
                      <span className={`text-2xl font-bold font-mono ${
                        incomeStatement.utilidad_neta >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {formatCurrency(incomeStatement.utilidad_neta)}
                      </span>
                    </div>
                    <div className="mt-2 text-right">
                      <span className="text-sm text-slate-600">Margen Neto: </span>
                      <span className={`text-lg font-bold font-mono ${
                        incomeStatement.margen_neto >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {incomeStatement.margen_neto.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Waterfall Chart */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                    Flujo de Ingresos a Utilidad (Waterfall)
                  </h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={[
                        { name: 'Ingresos', value: incomeStatement.ingresos, color: '#10b981' },
                        { name: 'Costos', value: -incomeStatement.costos, color: '#ef4444' },
                        { name: 'Utilidad Bruta', value: incomeStatement.utilidad_bruta, color: '#3b82f6' },
                        { name: 'Gastos', value: -incomeStatement.gastos_operativos, color: '#f59e0b' },
                        { name: 'Utilidad Neta', value: incomeStatement.utilidad_neta, color: incomeStatement.utilidad_neta >= 0 ? '#10b981' : '#ef4444' }
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="name" 
                        angle={-15}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(Math.abs(value))}
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {[
                          { name: 'Ingresos', value: incomeStatement.ingresos, color: '#10b981' },
                          { name: 'Costos', value: -incomeStatement.costos, color: '#ef4444' },
                          { name: 'Utilidad Bruta', value: incomeStatement.utilidad_bruta, color: '#3b82f6' },
                          { name: 'Gastos', value: -incomeStatement.gastos_operativos, color: '#f59e0b' },
                          { name: 'Utilidad Neta', value: incomeStatement.utilidad_neta, color: incomeStatement.utilidad_neta >= 0 ? '#10b981' : '#ef4444' }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Balance General */}
        <TabsContent value="balance-sheet" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Configuración del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tienda</Label>
                  <select
                    value={filters.store_id}
                    onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="all">Consolidado (Todas)</option>
                    {stores.filter(s => s.is_active).map(store => (
                      <option key={`balance-store-${store.id}`} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Corte</Label>
                  <Input
                    type="date"
                    value={filters.date}
                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button onClick={fetchBalanceSheet} className="btn-primary" disabled={loading}>
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
                {balanceSheet && (
                  <Button onClick={() => downloadExcel('balance-sheet')} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Excel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {balanceSheet && (
            <Card className="hover-lift" data-testid="balance-sheet-result">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-900">
                  Balance General - {balanceSheet.store_name}
                </CardTitle>
                <p className="text-sm text-slate-600">Al {filters.date}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Activos */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-900 border-b-2 border-blue-600 pb-2">ACTIVOS</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2">
                        <span className="font-semibold text-slate-900">Total Activos</span>
                        <span className="font-bold text-blue-700 font-mono text-lg">
                          {formatCurrency(balanceSheet.activos.total)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Pasivos y Patrimonio */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-900 border-b-2 border-green-600 pb-2">PASIVOS Y PATRIMONIO</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between items-center py-2 border-b border-slate-200">
                          <span className="font-semibold text-slate-900">PASIVOS</span>
                          <span className="font-bold text-red-700 font-mono">
                            {formatCurrency(balanceSheet.pasivos.total)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 mb-2">PATRIMONIO</div>
                        <div className="pl-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Capital</span>
                            <span className="font-mono text-slate-900">
                              {formatCurrency(balanceSheet.patrimonio.capital)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Utilidad del Periodo</span>
                            <span className={`font-mono ${
                              balanceSheet.patrimonio.utilidad_periodo >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {formatCurrency(balanceSheet.patrimonio.utilidad_periodo)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-t border-slate-200">
                            <span className="font-semibold text-slate-900">Total Patrimonio</span>
                            <span className="font-bold text-green-700 font-mono">
                              {formatCurrency(balanceSheet.patrimonio.total)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border-2 border-green-200 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-slate-900">TOTAL PASIVO + PATRIMONIO</span>
                          <span className="font-bold text-green-700 font-mono text-lg">
                            {formatCurrency(balanceSheet.total_pasivo_patrimonio)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Libro Mayor / Partida Contable */}
        <TabsContent value="libro-mayor" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Libro Mayor - Partida Contable
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Filtrar por Tipo</Label>
                  <select
                    value={libroMayorFilter}
                    onChange={(e) => setLibroMayorFilter(e.target.value)}
                    className="w-full p-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-600"
                  >
                    <option value="">Todos</option>
                    <option value="INGRESO">Solo Ingresos</option>
                    <option value="GASTO">Solo Gastos</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Modo Vista</Label>
                  <select
                    value={libroMayorViewMode}
                    onChange={(e) => setLibroMayorViewMode(e.target.value)}
                    className="w-full p-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-600"
                  >
                    <option value="grouped">Agrupado por Cuenta</option>
                    <option value="ungrouped">Lista Completa</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={fetchLibroMayor}
                    disabled={loading}
                    className="w-full"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {loading ? 'Generando...' : 'Generar Libro Mayor'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {libroMayor && (
            <Card className="overflow-hidden" data-testid="libro-mayor-result">
              {/* Header con Resumen General */}
              <CardHeader className="bg-slate-800 text-white p-6">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <BookOpen className="h-6 w-6" />
                      LIBRO MAYOR
                    </CardTitle>
                    <p className="text-slate-300 text-sm mt-1">
                      Periodo: {filters.start_date} al {filters.end_date}
                    </p>
                  </div>
                  <Button
                    onClick={() => downloadLibroMayorPDF()}
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar PDF
                  </Button>
                </div>
                
                {/* Resumen General en Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-4">
                    <p className="text-green-300 text-xs font-medium uppercase">Total Ingresos</p>
                    <p className="text-2xl font-bold font-mono text-green-400">
                      {formatCurrency(libroMayor.summary.total_ingresos)}
                    </p>
                  </div>
                  <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4">
                    <p className="text-red-300 text-xs font-medium uppercase">Total Gastos</p>
                    <p className="text-2xl font-bold font-mono text-red-400">
                      {formatCurrency(libroMayor.summary.total_gastos)}
                    </p>
                  </div>
                  <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-blue-300 text-xs font-medium uppercase">Saldo Bancos</p>
                    <p className="text-2xl font-bold font-mono text-blue-400">
                      {formatCurrency(libroMayor.summary.total_bancos || 0)}
                    </p>
                  </div>
                  <div className={`rounded-lg p-4 ${
                    libroMayor.summary.utilidad_bruta >= 0 
                      ? 'bg-emerald-600/20 border border-emerald-500/30' 
                      : 'bg-orange-600/20 border border-orange-500/30'
                  }`}>
                    <p className={`text-xs font-medium uppercase ${
                      libroMayor.summary.utilidad_bruta >= 0 ? 'text-emerald-300' : 'text-orange-300'
                    }`}>Utilidad Bruta</p>
                    <p className={`text-2xl font-bold font-mono ${
                      libroMayor.summary.utilidad_bruta >= 0 ? 'text-emerald-400' : 'text-orange-400'
                    }`}>
                      {formatCurrency(libroMayor.summary.utilidad_bruta)}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                {/* Categorías del Libro Mayor */}
                {libroMayor.categories && (
                  <div className="divide-y divide-slate-200">
                    {/* Orden de categorías para mostrar */}
                    {['INGRESOS', 'BANCOS', 'COMPRAS', 'SALARIOS', 'PRESTACIONES', 'GASTOS_OPERATIVOS', 'ACTIVOS', 'PASIVOS', 'OTROS']
                      .filter(catKey => libroMayor.categories[catKey] && libroMayor.categories[catKey].accounts.length > 0)
                      .map((catKey) => {
                        const category = libroMayor.categories[catKey];
                        const isExpanded = expandedAccounts[`cat_${catKey}`];
                        
                        // Colores por categoría
                        const categoryColors = {
                          'INGRESOS': { bg: 'bg-green-600', text: 'text-green-600', light: 'bg-green-50', border: 'border-green-200' },
                          'BANCOS': { bg: 'bg-blue-600', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200' },
                          'COMPRAS': { bg: 'bg-orange-600', text: 'text-orange-600', light: 'bg-orange-50', border: 'border-orange-200' },
                          'SALARIOS': { bg: 'bg-purple-600', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-200' },
                          'PRESTACIONES': { bg: 'bg-pink-600', text: 'text-pink-600', light: 'bg-pink-50', border: 'border-pink-200' },
                          'GASTOS_OPERATIVOS': { bg: 'bg-red-600', text: 'text-red-600', light: 'bg-red-50', border: 'border-red-200' },
                          'ACTIVOS': { bg: 'bg-cyan-600', text: 'text-cyan-600', light: 'bg-cyan-50', border: 'border-cyan-200' },
                          'PASIVOS': { bg: 'bg-amber-600', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-200' },
                          'OTROS': { bg: 'bg-slate-600', text: 'text-slate-600', light: 'bg-slate-50', border: 'border-slate-200' }
                        };
                        const colors = categoryColors[catKey] || categoryColors['OTROS'];

                        return (
                          <div key={catKey} className="border-b border-slate-200 last:border-b-0">
                            {/* Header de Categoría - Clickeable para expandir */}
                            <div 
                              className={`cursor-pointer transition-all hover:bg-slate-50 ${isExpanded ? 'bg-slate-50' : ''}`}
                              onClick={() => toggleAccountExpanded(`cat_${catKey}`)}
                            >
                              <div className="flex items-center justify-between px-6 py-4">
                                <div className="flex items-center gap-4">
                                  <div className={`w-2 h-12 rounded-full ${colors.bg}`}></div>
                                  <div>
                                    <h3 className="font-bold text-lg text-slate-900">{category.title}</h3>
                                    <p className="text-sm text-slate-500">{category.subtitle}</p>
                                    <p className="text-xs text-slate-400 mt-1">
                                      {category.accounts_with_movement} cuentas con movimiento de {category.account_count} total
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className={`text-2xl font-bold font-mono ${colors.text}`}>
                                      {formatCurrency(category.total)}
                                    </p>
                                  </div>
                                  {isExpanded 
                                    ? <ChevronDown className="h-6 w-6 text-slate-400" /> 
                                    : <ChevronRight className="h-6 w-6 text-slate-400" />
                                  }
                                </div>
                              </div>
                            </div>

                            {/* Contenido Expandido - Tabla de Cuentas */}
                            {isExpanded && (
                              <div className={`${colors.light} border-t ${colors.border}`}>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="bg-slate-100 text-slate-700">
                                        <th className="text-left px-4 py-3 font-semibold w-28">Codigo</th>
                                        <th className="text-left px-4 py-3 font-semibold">Nombre de Cuenta</th>
                                        <th className="text-center px-4 py-3 font-semibold w-24">Mov.</th>
                                        <th className="text-right px-4 py-3 font-semibold w-36">Total</th>
                                        <th className="text-center px-4 py-3 font-semibold w-20">Detalle</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                      {category.accounts.map((account) => {
                                        const accExpanded = expandedAccounts[`acc_${account.account_code}`];
                                        const showAccount = account.has_movements || account.is_bank_balance || account.total !== 0;
                                        
                                        if (!showAccount && !libroMayorViewMode.includes('all')) {
                                          // Mostrar cuentas con saldo 0 en una fila simple
                                          return (
                                            <tr key={account.account_code} className="bg-white hover:bg-slate-50">
                                              <td className="px-4 py-2 font-mono text-xs text-slate-400">{account.account_code}</td>
                                              <td className="px-4 py-2 text-slate-500">{account.account_name}</td>
                                              <td className="px-4 py-2 text-center text-slate-400">0</td>
                                              <td className="px-4 py-2 text-right font-mono text-slate-400">Q 0.00</td>
                                              <td className="px-4 py-2 text-center">-</td>
                                            </tr>
                                          );
                                        }

                                        return (
                                          <React.Fragment key={account.account_code}>
                                            {/* Fila de la Cuenta */}
                                            <tr className={`bg-white hover:bg-slate-50 ${accExpanded ? 'bg-slate-50' : ''}`}>
                                              <td className="px-4 py-3 font-mono text-sm font-medium text-slate-700">
                                                {account.account_code}
                                              </td>
                                              <td className="px-4 py-3">
                                                <div>
                                                  <span className="font-medium text-slate-900">{account.account_name}</span>
                                                  {account.is_bank_balance && (
                                                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                                      Saldo Actual
                                                    </span>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 text-center">
                                                <span className="px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded-full font-medium">
                                                  {account.transaction_count}
                                                </span>
                                              </td>
                                              <td className={`px-4 py-3 text-right font-mono font-bold ${colors.text}`}>
                                                {formatCurrency(account.total)}
                                              </td>
                                              <td className="px-4 py-3 text-center">
                                                {account.by_store && account.by_store.length > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      toggleAccountExpanded(`acc_${account.account_code}`);
                                                    }}
                                                    className="p-1 hover:bg-slate-200 rounded transition-colors"
                                                  >
                                                    {accExpanded 
                                                      ? <ChevronDown className="h-4 w-4 text-slate-500" />
                                                      : <ChevronRight className="h-4 w-4 text-slate-500" />
                                                    }
                                                  </button>
                                                )}
                                              </td>
                                            </tr>

                                            {/* Detalle por Tienda (expandible) */}
                                            {accExpanded && account.by_store && account.by_store.length > 0 && (
                                              <tr>
                                                <td colSpan="5" className="p-0">
                                                  <div className="bg-slate-100 border-y border-slate-200">
                                                    <table className="w-full text-xs">
                                                      <thead>
                                                        <tr className="bg-slate-200 text-slate-600">
                                                          <th className="text-left px-6 py-2 font-medium">Tienda</th>
                                                          <th className="text-center px-4 py-2 font-medium">Transacciones</th>
                                                          <th className="text-right px-6 py-2 font-medium">Subtotal</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {account.by_store.map((store) => (
                                                          <tr key={store.store_id} className="border-b border-slate-200 last:border-b-0 hover:bg-white">
                                                            <td className="px-6 py-2 text-slate-700 font-medium">
                                                              {store.store_name}
                                                            </td>
                                                            <td className="px-4 py-2 text-center text-slate-500">
                                                              {store.count}
                                                            </td>
                                                            <td className={`px-6 py-2 text-right font-mono font-medium ${colors.text}`}>
                                                              {formatCurrency(store.total)}
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr className={`${colors.light} font-bold`}>
                                        <td colSpan="3" className="px-4 py-3 text-right text-slate-700">
                                          TOTAL {category.title}:
                                        </td>
                                        <td className={`px-4 py-3 text-right font-mono text-lg ${colors.text}`}>
                                          {formatCurrency(category.total)}
                                        </td>
                                        <td></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* Resumen Final */}
                <div className="p-6 bg-slate-800 text-white">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="md:col-span-3">
                      <h4 className="font-bold text-lg mb-3">Resumen del Libro Mayor</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400">Cuentas Totales</p>
                          <p className="font-bold text-xl">{libroMayor.summary.total_cuentas}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Movimientos</p>
                          <p className="font-bold text-xl">{libroMayor.summary.total_transacciones}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Ingresos</p>
                          <p className="font-bold text-xl text-green-400">{formatCurrency(libroMayor.summary.total_ingresos)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Gastos</p>
                          <p className="font-bold text-xl text-red-400">{formatCurrency(libroMayor.summary.total_gastos)}</p>
                        </div>
                      </div>
                    </div>
                    <div className={`flex flex-col justify-center items-center p-4 rounded-lg ${
                      libroMayor.summary.utilidad_bruta >= 0 
                        ? 'bg-green-600/30 border border-green-500/50' 
                        : 'bg-red-600/30 border border-red-500/50'
                    }`}>
                      <p className="text-slate-300 text-sm">Balance Neto</p>
                      <p className={`text-3xl font-bold font-mono ${
                        libroMayor.summary.utilidad_bruta >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formatCurrency(libroMayor.summary.utilidad_bruta)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reporte de IVA */}
        <TabsContent value="iva-summary" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Configuración del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={fetchIvaSummary} className="w-full btn-primary" disabled={loading}>
                {loading ? 'Generando...' : 'Generar Reporte de IVA'}
              </Button>
            </CardContent>
          </Card>

          {ivaSummary && (
            <Card>
              <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Reporte de IVA
                </CardTitle>
                <p className="text-blue-100 text-sm">
                  Período: {ivaSummary.period.start_date} al {ivaSummary.period.end_date}
                </p>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Resumen General */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-200">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Resumen General de IVA</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-3 border-b border-blue-200">
                      <span className="text-slate-700 font-medium">IVA por Cobrar (Ventas)</span>
                      <span className="font-bold text-green-700 font-mono text-lg">
                        {formatCurrency(ivaSummary.resumen.iva_por_cobrar)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-blue-200">
                      <span className="text-slate-700 font-medium">IVA por Pagar (Compras)</span>
                      <span className="font-bold text-red-700 font-mono text-lg">
                        {formatCurrency(ivaSummary.resumen.iva_por_pagar)}
                      </span>
                    </div>
                    <div className={`flex justify-between items-center py-4 px-4 rounded-lg ${
                      ivaSummary.resumen.iva_neto >= 0 
                        ? 'bg-green-100 border-2 border-green-300' 
                        : 'bg-red-100 border-2 border-red-300'
                    }`}>
                      <span className="text-slate-900 font-bold text-lg">IVA Neto</span>
                      <span className={`font-bold font-mono text-xl ${
                        ivaSummary.resumen.iva_neto >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {formatCurrency(ivaSummary.resumen.iva_neto)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 italic mt-2 bg-white p-3 rounded border border-blue-200">
                      📌 {ivaSummary.resumen.nota}
                    </p>
                  </div>
                </div>

                {/* Desglose por Tienda */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Desglose por Tienda</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-100 border-b-2 border-slate-300">
                          <th className="text-left p-3 font-semibold text-slate-700">Tienda</th>
                          <th className="text-right p-3 font-semibold text-slate-700">IVA por Cobrar</th>
                          <th className="text-right p-3 font-semibold text-slate-700">IVA por Pagar</th>
                          <th className="text-right p-3 font-semibold text-slate-700">IVA Neto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ivaSummary.por_tienda.map((store, index) => (
                          <tr key={index} className="border-b border-slate-200 hover:bg-slate-50">
                            <td className="p-3 font-medium text-slate-900">{store.store_name}</td>
                            <td className="p-3 text-right font-mono text-green-700">
                              {formatCurrency(store.iva_por_cobrar)}
                            </td>
                            <td className="p-3 text-right font-mono text-red-700">
                              {formatCurrency(store.iva_por_pagar)}
                            </td>
                            <td className={`p-3 text-right font-mono font-bold ${
                              store.iva_neto >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {formatCurrency(store.iva_neto)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reporte por Cuenta Específica */}
        <TabsContent value="by-account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Configuración del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tienda</Label>
                  <select
                    value={filters.store_id}
                    onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="all">Consolidado (Todas)</option>
                    {stores.filter(s => s.is_active).map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Códigos de Cuenta (opcional)</Label>
                <Input
                  value={accountCodes}
                  onChange={(e) => setAccountCodes(e.target.value)}
                  placeholder="Ej: 6010101, 6010102 (separados por coma) o deja vacío para ver todas"
                />
                <p className="text-xs text-slate-500">
                  Ingresa los códigos de las cuentas que deseas analizar (Ej: Alquiler, Sistemas, Internet). Deja vacío para ver resumen de todas las cuentas.
                </p>
              </div>
              <Button onClick={fetchByAccountReport} className="w-full btn-primary" disabled={loading}>
                {loading ? 'Generando...' : 'Generar Reporte por Cuenta'}
              </Button>
            </CardContent>
          </Card>

          {byAccountReport && (
            <Card>
              <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600">
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Análisis por Cuenta
                </CardTitle>
                <p className="text-indigo-100 text-sm">
                  Período: {byAccountReport.period.start_date} al {byAccountReport.period.end_date}
                </p>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Resumen Comparativo */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                    <p className="text-sm text-green-700 font-medium">Total Ventas</p>
                    <p className="text-2xl font-bold text-green-900 font-mono">
                      {formatCurrency(byAccountReport.total_ventas)}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                    <p className="text-sm text-red-700 font-medium">Total Gastos</p>
                    <p className="text-2xl font-bold text-red-900 font-mono">
                      {formatCurrency(byAccountReport.total_gastos)}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg border-2 ${
                    byAccountReport.diferencia >= 0 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <p className="text-sm font-medium">Diferencia</p>
                    <p className={`text-2xl font-bold font-mono ${
                      byAccountReport.diferencia >= 0 ? 'text-blue-900' : 'text-orange-900'
                    }`}>
                      {formatCurrency(byAccountReport.diferencia)}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                    <p className="text-sm text-purple-700 font-medium">Margen</p>
                    <p className="text-2xl font-bold text-purple-900 font-mono">
                      {byAccountReport.margen_porcentaje}%
                    </p>
                  </div>
                </div>

                {/* Detalle por Cuenta */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Detalle por Cuenta</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-100 border-b-2 border-slate-300">
                          <th className="text-left p-3 font-semibold text-slate-700">Código</th>
                          <th className="text-left p-3 font-semibold text-slate-700">Nombre de Cuenta</th>
                          <th className="text-right p-3 font-semibold text-slate-700">Total</th>
                          <th className="text-right p-3 font-semibold text-slate-700">Transacciones</th>
                          <th className="text-right p-3 font-semibold text-slate-700">% del Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byAccountReport.cuentas.map((cuenta, index) => (
                          <tr key={index} className="border-b border-slate-200 hover:bg-slate-50">
                            <td className="p-3 font-mono text-sm">{cuenta.codigo}</td>
                            <td className="p-3 font-medium text-slate-900">{cuenta.nombre}</td>
                            <td className="p-3 text-right font-mono text-red-700 font-bold">
                              {formatCurrency(cuenta.total)}
                            </td>
                            <td className="p-3 text-right text-slate-600">
                              {cuenta.num_transacciones}
                            </td>
                            <td className="p-3 text-right font-mono text-slate-700">
                              {((cuenta.total / byAccountReport.total_gastos) * 100).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                          <td colSpan="2" className="p-3">TOTAL</td>
                          <td className="p-3 text-right font-mono text-red-900">
                            {formatCurrency(byAccountReport.total_gastos)}
                          </td>
                          <td className="p-3 text-right">
                            {byAccountReport.cuentas.reduce((sum, c) => sum + c.num_transacciones, 0)}
                          </td>
                          <td className="p-3 text-right">100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Gastos por Tienda */}
        <TabsContent value="expenses" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Configuración del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button onClick={fetchExpensesByStore} className="btn-primary" disabled={loading}>
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
                {expensesByStore && (
                  <Button onClick={() => downloadExcel('expenses-by-store')} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Excel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {expensesByStore && (
            <Card className="hover-lift" data-testid="expenses-by-store-result">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-900">Gastos por Tienda</CardTitle>
                <p className="text-sm text-slate-600">
                  Periodo: {filters.start_date} a {filters.end_date}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.values(expensesByStore.expenses_by_store).map((store) => (
                    <Card key={store.store_name} className="stat-card">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold text-slate-900">
                          {store.store_name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Total Gastos:</span>
                          <span className="font-bold text-red-700 font-mono">
                            {formatCurrency(store.total_expenses)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Transacciones:</span>
                          <span className="font-semibold text-slate-900">
                            {store.transaction_count}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pie Chart - Distribución de Gastos por Tienda */}
                {Object.values(expensesByStore.expenses_by_store).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                      <PieChart className="h-5 w-5 mr-2 text-orange-600" />
                      Distribución de Gastos por Tienda (%)
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <RechartsPieChart>
                        <Pie
                          data={Object.values(expensesByStore.expenses_by_store).map(store => ({
                            name: store.store_name,
                            value: store.total_expenses
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={(entry) => {
                            const total = Object.values(expensesByStore.expenses_by_store).reduce((sum, s) => sum + s.total_expenses, 0);
                            return `${entry.name}: ${((entry.value / total) * 100).toFixed(1)}%`;
                          }}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {Object.values(expensesByStore.expenses_by_store).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'][index % 8]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>


        {/* GASTOS DETALLADOS TAB */}
        <TabsContent value="expenses-detailed">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Gastos Detallados por Tienda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tienda</Label>
                  <select
                    value={filters.store_id}
                    onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="all">Consolidado (Todas)</option>
                    {stores.filter(s => s.is_active).map(store => (
                      <option key={`detailed-store-${store.id}`} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={fetchExpensesDetailed} disabled={loading} className="btn-primary">
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
                {expensesDetailed && (
                  <Button onClick={() => downloadExcel('expenses-detailed')} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Excel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {expensesDetailed && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-600">
                  Período: {expensesDetailed.period.start_date} - {expensesDetailed.period.end_date}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {expensesDetailed.expenses_by_store.map((store) => (
                  <div key={store.store_id} className="mb-8">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b-2 border-blue-600">
                      {store.store_name}
                    </h3>
                    
                    {store.by_account.map((account) => (
                      <div key={account.account_code} className="mb-6">
                        <h4 className="text-md font-semibold text-slate-700 mb-2 bg-slate-100 p-2 rounded">
                          {account.account_code} - {account.account_name}
                        </h4>
                        
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="text-left py-2 px-3 font-medium text-slate-600">Fecha</th>
                                <th className="text-left py-2 px-3 font-medium text-slate-600">Descripción</th>
                                <th className="text-right py-2 px-3 font-medium text-slate-600">Monto</th>
                                <th className="text-right py-2 px-3 font-medium text-slate-600">IVA</th>
                                <th className="text-right py-2 px-3 font-medium text-slate-600">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {account.transactions.map((trans) => (
                                <tr key={trans.id} className="border-b border-slate-100">
                                  <td className="py-2 px-3 text-slate-700">
                                    {new Date(trans.date).toLocaleDateString('es-GT')}
                                  </td>
                                  <td className="py-2 px-3 text-slate-700">{trans.description}</td>
                                  <td className="py-2 px-3 text-right font-mono">{formatCurrency(trans.amount)}</td>
                                  <td className="py-2 px-3 text-right font-mono">{formatCurrency(trans.iva_amount)}</td>
                                  <td className="py-2 px-3 text-right font-mono font-semibold">{formatCurrency(trans.total_amount)}</td>
                                </tr>
                              ))}
                              <tr className="bg-blue-50 font-semibold">
                                <td colSpan="4" className="py-2 px-3 text-right text-slate-700">Subtotal {account.account_name}:</td>
                                <td className="py-2 px-3 text-right font-mono text-blue-700">{formatCurrency(account.total)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                    
                    <div className="bg-slate-800 text-white p-3 rounded font-bold flex justify-between">
                      <span>TOTAL {store.store_name.toUpperCase()}</span>
                      <span className="font-mono">{formatCurrency(store.total_expenses)}</span>
                    </div>
                  </div>
                ))}
                
                <div className="bg-blue-600 text-white p-4 rounded-lg font-bold text-lg flex justify-between mt-6">
                  <span>TOTAL GENERAL</span>
                  <span className="font-mono">{formatCurrency(expensesDetailed.summary.grand_total)}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* RENTABILIDAD TAB */}
        <TabsContent value="profitability">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Análisis de Rentabilidad por Tienda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={fetchProfitability} disabled={loading} className="btn-primary">
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
                {profitability && (
                  <>
                    <Button onClick={() => downloadExcel('profitability')} variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Descargar Excel
                    </Button>
                    <Button onClick={() => downloadPDF('profitability')} variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Descargar PDF
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {profitability && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-600">
                  Período: {profitability.period.start_date} - {profitability.period.end_date}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Tienda</th>
                        <th className="text-right py-3 px-4 font-semibold">Ingresos</th>
                        <th className="text-right py-3 px-4 font-semibold">Costos</th>
                        <th className="text-right py-3 px-4 font-semibold">Gastos</th>
                        <th className="text-right py-3 px-4 font-semibold">Utilidad Bruta</th>
                        <th className="text-right py-3 px-4 font-semibold">Utilidad Neta</th>
                        <th className="text-right py-3 px-4 font-semibold">Margen Bruto</th>
                        <th className="text-right py-3 px-4 font-semibold">Margen Neto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitability.by_store.map((store) => (
                        <tr key={store.store_id} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium text-slate-900">{store.store_name}</td>
                          <td className="py-3 px-4 text-right font-mono">{formatCurrency(store.ingresos)}</td>
                          <td className="py-3 px-4 text-right font-mono text-red-700">{formatCurrency(store.costos)}</td>
                          <td className="py-3 px-4 text-right font-mono text-red-700">{formatCurrency(store.gastos)}</td>
                          <td className="py-3 px-4 text-right font-mono text-blue-700">{formatCurrency(store.utilidad_bruta)}</td>
                          <td className="py-3 px-4 text-right font-mono text-green-700 font-semibold">{formatCurrency(store.utilidad_neta)}</td>
                          <td className="py-3 px-4 text-right font-mono">{store.margen_bruto.toFixed(1)}%</td>
                          <td className="py-3 px-4 text-right font-mono font-semibold">
                            <span className={store.margen_neto >= 0 ? 'text-green-700' : 'text-red-700'}>
                              {store.margen_neto.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-blue-600 text-white font-bold">
                        <td className="py-3 px-4">CONSOLIDADO</td>
                        <td className="py-3 px-4 text-right font-mono">{formatCurrency(profitability.consolidated.ingresos)}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatCurrency(profitability.consolidated.costos)}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatCurrency(profitability.consolidated.gastos)}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatCurrency(profitability.consolidated.utilidad_bruta)}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatCurrency(profitability.consolidated.utilidad_neta)}</td>
                        <td className="py-3 px-4 text-right font-mono">{profitability.consolidated.margen_bruto.toFixed(1)}%</td>
                        <td className="py-3 px-4 text-right font-mono">{profitability.consolidated.margen_neto.toFixed(1)}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Bar Chart Comparativo */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                    Comparativo de Rentabilidad por Tienda
                  </h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={profitability.by_store}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="store_name" 
                        angle={-15}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Bar dataKey="ingresos" name="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="gastos" name="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="utilidad_neta" name="Utilidad Neta" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-600">Utilidad Neta Total</p>
                          <p className="text-2xl font-bold text-green-700">{formatCurrency(profitability.consolidated.utilidad_neta)}</p>
                        </div>
                        <TrendingUp className="h-12 w-12 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-600">Margen Neto Promedio</p>
                          <p className="text-2xl font-bold text-blue-700">{profitability.consolidated.margen_neto.toFixed(1)}%</p>
                        </div>
                        <PieChart className="h-12 w-12 text-blue-600" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default Reports;
